import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import SettingsIcon from '@mui/icons-material/Settings'
import { settingsMenuItem } from '../../helpers/Constants'
import { hasAnyRole } from '../../helpers/permissions'
import useAuth from '../../hooks/useAuth'
import { BRAND, ICON, SURFACE, TEXT, cardSx } from '../../theme/tokens'
import ErrorBanner from './ErrorBanner'

/**
 * What an AI tool shows instead of itself while the model cannot be used:
 * checking, no key configured, or the check failed (with its reason). Shared
 * by every tool that needs the key, so the copies cannot drift.
 *
 * Only a user who can open Settings (where the key is saved) is sent there;
 * anyone else is told to ask an admin, since the Settings link would only
 * have shown them "not authorised".
 */
const AiStatusGate = ({ status }) => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const canOpenSettings = hasAnyRole(
        user?.permissions,
        settingsMenuItem.permissions
    )
    if (status.state === 'checking') {
        return (
            <Box
                role="status"
                sx={{ display: 'flex', justifyContent: 'center', py: 12 }}
            >
                <CircularProgress size={32} sx={{ color: BRAND.orange }} />
            </Box>
        )
    }
    const missing = status.state === 'missing'
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <Box sx={{ ...cardSx, p: 4, maxWidth: 440, textAlign: 'center' }}>
                <SettingsIcon
                    sx={{ fontSize: ICON.xl, color: BRAND.orange, mb: 1.5 }}
                />
                <Typography variant="h6" sx={{ color: TEXT.primary, mb: 1 }}>
                    {missing
                        ? 'The AI model is not configured'
                        : 'AI settings could not be checked'}
                </Typography>
                {missing ? (
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.secondary, mb: 2 }}
                    >
                        No Claude or OpenAI API key is saved, so the AI tools
                        cannot run.
                    </Typography>
                ) : (
                    <Box sx={{ mb: 2, textAlign: 'left' }}>
                        <ErrorBanner
                            message={
                                status.reason || 'The AI settings check failed.'
                            }
                        />
                    </Box>
                )}
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.muted, mb: 3 }}
                >
                    {canOpenSettings
                        ? missing
                            ? 'Save a key in Settings to use the AI tools.'
                            : 'Check the AI settings, or try again.'
                        : 'Ask an admin to set up the AI model in Settings.'}
                </Typography>
                <Box
                    sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}
                >
                    {!missing && (
                        <Button variant="outlined" onClick={status.retry}>
                            Try again
                        </Button>
                    )}
                    {canOpenSettings && (
                        <Button
                            variant="contained"
                            onClick={() => navigate('/settings')}
                            sx={{ borderColor: SURFACE.border }}
                        >
                            Go to Settings
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

AiStatusGate.propTypes = {
    status: PropTypes.shape({
        state: PropTypes.oneOf(['checking', 'ready', 'missing', 'error'])
            .isRequired,
        reason: PropTypes.string,
        retry: PropTypes.func,
    }).isRequired,
}

export default AiStatusGate
